/**
 * RunPost Constants
 * Central place to manage content types, platform configurations, and related data.
 */

// ---------------------------------------------------------------------------
// Content Types
// ---------------------------------------------------------------------------

/**
 * Available content types for a post.
 * Each entry has a `value` (stored in state) and a `label` (shown in the UI).
 */
export const CONTENT_TYPES = [
  { value: "Content Only", label: "Content Only" },
  { value: "Image", label: "Image" },
  { value: "Video", label: "Video" },
  // { value: "Reels/Shorts", label: "Reels/Shorts" },
];

/** The content type value that requires NO media upload. */
export const CONTENT_TYPE_NO_MEDIA = "Content Only";

/** Content type values that use media uploads. */
export const CONTENT_TYPE_IMAGE = "Image";
export const CONTENT_TYPE_VIDEO = "Video";
export const CONTENT_TYPE_REELS = "Reels/Shorts";

// ---------------------------------------------------------------------------
// Media Upload Limits
// Per content type: per-platform limits + a safe default when no platform
// is selected yet. getEffectiveLimits() picks the most restrictive values
// across whichever platforms the user has selected.
// ---------------------------------------------------------------------------
export const MEDIA_LIMITS = {
  // ─── Image ────────────────────────────────────────────────────────────────
  // Facebook : up to 30 MB, 10 photos/post
  // Instagram: up to  8 MB, 10 images/carousel
  // LinkedIn : up to  5 MB,  9 images/post
  Image: {
    accept: "image/*",
    formats: "JPG, PNG, GIF, WebP",
    default: { maxFiles: 9, maxSizeMB: 5, durationSec: null },
    platforms: {
      Facebook:  { maxFiles: 10, maxSizeMB: 30, durationSec: null },
      Instagram: { maxFiles: 10, maxSizeMB: 8,  durationSec: null },
      LinkedIn:  { maxFiles: 9,  maxSizeMB: 5,  durationSec: null },
    },
  },

  // ─── Reels / Shorts ───────────────────────────────────────────────────────
  // Facebook Reels : max 90 s, ~4 GB
  // Instagram Reels: max 90 s,  1 GB
  // YouTube Shorts : max 60 s, 256 MB (resumable upload API)
  "Reels/Shorts": {
    accept: "video/*",
    formats: "MP4, MOV",
    default: { maxFiles: 1, maxSizeMB: 256, durationSec: 60 },
    platforms: {
      Facebook:  { maxFiles: 1, maxSizeMB: 4096, durationSec: 90 },
      Instagram: { maxFiles: 1, maxSizeMB: 1024, durationSec: 90 },
      YouTube:   { maxFiles: 1, maxSizeMB: 256,  durationSec: 60 },
    },
  },

  // ─── Video ────────────────────────────────────────────────────────────────
  // LinkedIn    : up to 4 MB for current implementation
  // Note: Only LinkedIn is supported for video uploads in Run Now
  Video: {
    accept: "video/*",
    formats: "MP4, MOV",
    default: { maxFiles: 1, maxSizeMB: 4, durationSec: null },
    platforms: {
      LinkedIn:  { maxFiles: 1, maxSizeMB: 4, durationSec: null },
    },
  },
};

/**
 * Returns the effective (most restrictive) upload limits for the given content
 * type based on the currently selected platforms.
 *
 * - If no platforms are selected the `default` limits are returned.
 * - For each numeric field (maxFiles, maxSizeMB, durationSec) the minimum
 *   value across all selected platforms is used so every platform is satisfied.
 * - Platforms not found in the map fall back to the type's `default`.
 */
export const getEffectiveLimits = (contentType, selectedPlatforms = []) => {
  const typeLimits = MEDIA_LIMITS[contentType];
  if (!typeLimits) return null;

  const { default: defaults, platforms, accept, formats } = typeLimits;

  // No platforms chosen yet – return the safe defaults
  if (!selectedPlatforms.length || !platforms) {
    return { ...defaults, accept, formats };
  }

  // Resolve per-platform limits, falling back to defaults for unknown entries
  const entries = selectedPlatforms.map((p) => ({ ...defaults, ...(platforms[p] ?? {}) }));

  const maxFiles  = Math.min(...entries.map((e) => e.maxFiles));
  const maxSizeMB = Math.min(...entries.map((e) => e.maxSizeMB));

  // Only apply a duration cap when at least one selected platform defines one
  const durations = entries.map((e) => e.durationSec).filter((d) => d !== null && d !== undefined);
  const durationSec = durations.length ? Math.min(...durations) : null;

  return { maxFiles, maxSizeMB, durationSec, accept, formats };
};

// ---------------------------------------------------------------------------
// Platform Configurations
// Defines which platforms are available for each content type.
// ---------------------------------------------------------------------------

/**
 * Uncomment the broader config below when Twitter / YouTube support is re-added.
 *
 * export const platformConfigurations = {
 *   "Content Only": ["Facebook", "Twitter (X)", "LinkedIn"],
 *   Image: ["Facebook", "Instagram", "Twitter (X)", "LinkedIn"],
 *   Video: ["Facebook", "Instagram", "Twitter (X)", "YouTube", "LinkedIn"],
 * };
 */

// Updated 17th Oct 2025 – Twitter temporarily removed
export const platformConfigurations = {
  "Content Only": ["Facebook"],
  Image: ["Facebook", "Instagram", "LinkedIn"],
  "Reels/Shorts": ["Facebook", "Instagram", "YouTube"],
  Video: ["Facebook", "Instagram", "LinkedIn", "YouTube"],
};
